import type { QueryClient } from '@tanstack/react-query';

/**
 * Server-rendered route data — phases.md §1.10 and Phases 8–10.
 *
 * A public page must arrive as complete HTML: a crawler, a WhatsApp preview and a reader
 * on a slow connection all see the stories in the first response, not after a round trip.
 * So the server resolves what the route needs **in-process** (`apps/api/src/ssr-data.ts`
 * calls the Phase 6 services directly), renders with it, and serializes the same objects
 * into the document. The client writes them into its query cache instead of refetching
 * what it already has.
 *
 * The payload is a list of **cache entries** rather than named fields, so adding a page
 * is a resolver on the server and nothing here. The keys come from `packages/shared`
 * (`queryKeys`), which is what keeps the two halves in step.
 *
 * Deliberately not TanStack's `dehydrate`: the payload is a plain object either way, and
 * this shape is one a person can read in "view source" rather than a library's internals.
 */
export interface CacheEntry {
  key: readonly unknown[];
  data: unknown;
}

export interface RouteData {
  cache?: CacheEntry[];
  meta?: {
    title?: string;
    description?: string;
    ogType?: 'website' | 'article';
    /**
     * Set when the row itself decides how cacheable the page is — an ended live event is
     * an archive and caches like any other page, while a running one must not (§1.11).
     */
    cacheControl?: string;
    /** Absolute URL of the share image — the featured image, or a sitewide default. */
    ogImage?: string | null;
    /** Overrides the canonical the path implies — a district story (ADR-012). */
    canonical?: string | null;
    /** Structured data for this page (PRD §26), emitted as `application/ld+json`. */
    jsonLd?: unknown[];
    /** hreflang targets keyed by locale, for pages whose counterpart is at another slug. */
    alternates?: Record<string, string>;
    /** `article:published_time` / `article:modified_time`, for an article. */
    publishedTime?: string | null;
    modifiedTime?: string | null;
    section?: string | null;
    /** Search Console and Bing verification, and the publication's robots default. */
    verification?: { google?: string | null; bing?: string | null };
    robots?: string | null;
    twitterSite?: string | null;
    noindex?: boolean;
  };
  redirectTo?: string;
  notFound?: boolean;
}

/** The global the server writes and the client reads. */
export const ROUTE_DATA_GLOBAL = '__NK_DATA__';

/**
 * Seeds the query cache so the first client render uses the server's data and the first
 * `useQuery` does not refetch. `staleTime` still applies — the data is treated as fresh
 * for as long as §1.11 says the CDN may serve it.
 */
export function seedQueryClient(client: QueryClient, data: RouteData): void {
  for (const entry of data.cache ?? []) {
    client.setQueryData([...entry.key], entry.data);
  }
}

/**
 * Serializes route data for the document.
 *
 * `<` is escaped because a headline containing `</script>` would otherwise close the tag
 * and turn the rest of the payload into markup — the classic JSON-in-HTML injection, and
 * a real risk on a site whose content is written by dozens of people.
 *
 * The `nonce` is what lets this one inline script run under the Phase 17 policy. It is
 * empty in development, where CSP is off, and the attribute is then omitted rather than
 * written blank — an empty nonce attribute is a thing a strict policy rejects.
 */
export function serializeRouteData(data: RouteData, nonce = ''): string {
  const json = JSON.stringify(data).replace(/</g, '\\u003c');
  return `<script${nonceAttribute(nonce)}>window.${ROUTE_DATA_GLOBAL}=${json}</script>`;
}

/** ` nonce="…"`, or nothing at all. Shared with the JSON-LD blocks in `meta.ts`. */
export function nonceAttribute(nonce: string | undefined): string {
  return nonce ? ` nonce="${nonce}"` : '';
}
